import { BACKEND_URL, supabase } from "./supabase";

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

/**
 * Attend que la session Supabase soit HYDRATÉE (token présent) avant de rendre la main. Au cold-load, le
 * premier `getSession()` peut renvoyer une session nulle le temps que le SDK restaure le token du storage :
 * un appel authentifié parti à ce moment-là part SANS Authorization → 401 transitoire (miette P0-quater ④).
 * On boucle (backoff court) jusqu'au token ou au timeout, puis on rend `null` (le caller gère l'absence).
 */
export async function waitForAccessToken(maxMs = 2000): Promise<string | null> {
  const deadline = Date.now() + maxMs;
  for (;;) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) return token;
    if (Date.now() >= deadline) return null;
    await new Promise((r) => setTimeout(r, 150));
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // Attendre le token AVANT de partir (évite un premier /history sans Authorization → 401 transitoire).
  const token = await waitForAccessToken();

  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  // Same-origin proxy: /api/foo → /__bk/api/foo (évite CORS sur domaines custom).
  // Same-origin proxy: /api/foo → /api/public/bk/api/foo (évite CORS sur
  // domaines custom et contourne l'auth bridge Lovable côté preview).
  const url = path.startsWith("http")
    ? path
    : `${BACKEND_URL}/api/public/bk${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, { ...init, headers });

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  // 204 No Content (and any empty 2xx body) → return null without parsing.
  const hasBody = res.status !== 204 && res.status !== 205;
  const payload = hasBody
    ? isJson
      ? await res.json().catch(() => null)
      : await res.text().catch(() => "")
    : null;

  if (!res.ok) {
    const msg =
      (isJson && payload && typeof payload === "object" && "message" in (payload as Record<string, unknown>)
        ? String((payload as Record<string, unknown>).message)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(msg, res.status, payload);
  }

  return payload as T;
}
